import { useEffect, useState } from "react";
import { fetchAnnonces, fetchHotels, fetchStats } from "@/lib/api";
import { CATEGORIES, fmtPrice, withWatermark } from "@/lib/utils";
import { Link } from "react-router-dom";

export default function DashboardPage() {
  const [annonces, setAnnonces] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchAnnonces().then((d) => setAnnonces(d.items || d));
    fetchHotels().then(setHotels);
    fetchStats().then(setStats);
  }, []);

  const byCat = CATEGORIES.map((c) => ({
    key: c.key,
    count: (annonces.items || annonces).filter((a) => a.category === c.key).length,
  }));

  return (
    <main className="max-w-7xl mx-auto px-6 py-10" data-testid="dashboard-page">
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">Dashboard</h1>
      <p className="text-[var(--ink-soft)] mt-2">Apercu de la plateforme Fodindin.</p>

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            ["Visiteurs aujourd'hui", stats.visitors_today],
            ["Annonces actives", stats.active_annonces],
            ["Nouvelles / jour", stats.new_per_day],
            ["Hotels partenaires", stats.partner_hotels],
          ].map(([label, v]) => (
            <div key={label} className="glass-card p-5">
              <div className="text-xs uppercase tracking-widest text-[var(--ink-soft)]">{label}</div>
              <div className="text-2xl font-extrabold text-orange-600 mt-1">{v.toLocaleString("fr-FR")}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-6">
        <div className="glass-card p-7">
          <div className="font-bold text-lg mb-4">Annonces par categorie</div>
          <div className="space-y-3">
            {byCat.map((c) => {
              const max = Math.max(...byCat.map((x) => x.count), 1);
              const pct = (c.count / max) * 100;
              return (
                <div key={c.key}>
                  <div className="flex justify-between text-sm mb-1"><span>{c.key}</span><b>{c.count}</b></div>
                  <div className="h-2 rounded-full bg-orange-50 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-orange-500 to-orange-400" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="glass-card p-7">
          <div className="font-bold text-lg mb-4">Dernieres annonces</div>
          <div className="space-y-3 text-sm">
            {(annonces.items || annonces).slice(0, 6).map((a) => (
              <Link to={`/annonces/${a.id}`} key={a.id} className="flex items-center gap-3 hover:bg-orange-50/50 -mx-2 px-2 py-1.5 rounded-lg">
                <img src={withWatermark(a.image)} className="w-12 h-12 rounded-lg object-cover" alt="" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{a.title}</div>
                  <div className="text-xs text-[var(--ink-soft)]">{a.category} - {a.city}</div>
                </div>
                <div className="text-orange-600 font-bold whitespace-nowrap">{fmtPrice(a.price, a.price_unit)}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 glass-card p-7">
        <div className="font-bold text-lg mb-4">Hotels partenaires</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {hotels.map((h) => (
            <Link to={`/hotelleries/${h.id}`} key={h.id} className="card">
              <img src={withWatermark(h.image)} className="w-full h-36 object-cover" alt="" />
              <div className="p-3">
                <div className="font-bold text-sm">{h.name}</div>
                <div className="text-xs text-[var(--ink-soft)]">{h.city}</div>
                <div className="mt-1 text-orange-600 font-bold text-sm">{fmtPrice(h.price)} / nuit</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
